import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import useApi from '../../../../hooks/useApi';
import Message from '../../../../components/Message';
import { useToast } from '../../../../contexts/ToastContext';
import {
  getRevenueCategories,
  createRevenueCategory,
  updateRevenueCategory,
  deleteRevenueCategory,
} from '../../../../services/finance_services/revenueService';

const InviterRevenueCategoriesPage = () => {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editing, setEditing] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // API hooks
  const { loading, error, callApi: fetchCategories } = useApi(getRevenueCategories, { showSuccessToast: false });
  const { loading: creating, error: createError, callApi: createCategory } = useApi(createRevenueCategory, { 
    showSuccessToast: true, 
    msg: 'Category created successfully' 
  });
  const { loading: updating, error: updateError, callApi: updateCategory } = useApi(updateRevenueCategory, { 
    showSuccessToast: true, 
    msg: 'Category updated successfully' 
  });
  const { loading: deleting, error: deleteError, callApi: deleteCategory } = useApi(deleteRevenueCategory, { 
    showSuccessToast: true, 
    msg: 'Category deleted successfully' 
  });

  const loadCategories = async () => {
    const data = await fetchCategories(invitationId);
    if (data) {
      setCategories(data);
    }
  };

  useEffect(() => {
    loadCategories();
  }, [invitationId]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const categoryData = { name, description };
    
    try {
      if (editing) {
        await updateCategory(editing.id, categoryData, invitationId);
      } else {
        await createCategory(categoryData, invitationId);
      }
      resetForm();
      loadCategories();
    } catch (err) {
      console.error('Error saving category:', err);
    }
  };

  const handleEdit = (category) => {
    setEditing(category);
    setName(category.name);
    setDescription(category.description || '');
  };

  const handleDelete = (category) => {
    setDeletingCategory(category);
  };

  const confirmDelete = async () => {
    try {
      await deleteCategory(deletingCategory.id, invitationId);
      setDeletingCategory(null);
      loadCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setDeletingCategory(null);
    }
  };

  const actionLoading = creating || updating || deleting;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Revenue Categories</h1>
        </div>
      </div>

      {/* Error Messages */}
      {(error || createError || updateError || deleteError) && (
        <Message type="error" message={error || createError || updateError || deleteError} />
      )}

      {/* Add/Edit Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">
          {editing ? 'Edit Category' : 'Add New Category'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                placeholder="Enter category name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={actionLoading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                placeholder="Enter description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={actionLoading}
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={actionLoading || !name.trim()}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              {editing ? 'Update Category' : 'Add Category'}
            </button>
            
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No revenue categories found.</p>
            <p className="text-sm text-gray-400 mt-1">Create your first category using the form above.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {categories.map((category) => (
              <div key={category.id} className="p-6 flex items-center justify-between hover:bg-gray-50">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(category)}
                    disabled={actionLoading}
                    className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </button>
                  
                  <button
                    onClick={() => handleDelete(category)}
                    disabled={actionLoading}
                    className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Delete Category</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{deletingCategory.name}"? This action cannot be undone.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingCategory(null)}
                disabled={deleting}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviterRevenueCategoriesPage;